import { memo, useCallback, useState } from "react";
import { Button } from "../ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { PencilSimple, Lock, SignOut, Trash } from "@phosphor-icons/react";
import { ChevronDown } from "lucide-react";

// SALES-026: one cell at a time turns into a dropdown. A row shows its links as
// a static button styled exactly like the app's Select trigger (border, radius,
// padding, chevron) — visible at rest, no Select rendered per cell — so the page
// never holds more than one real dropdown and typing re-renders no rows.
function LinkCell({ isEditing, value, displayText, options, onStartEdit, onCancelEdit, onChange, ariaLabel, testId }) {
  if (!isEditing) {
    return (
      <button
        type="button"
        onClick={onStartEdit}
        aria-label={ariaLabel}
        data-testid={testId}
        className="flex h-9 w-full items-center justify-between gap-2 rounded-xl border border-border bg-input px-3 py-2 text-sm text-left transition-colors hover:border-primary/40 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary/50 focus-visible:border-primary/40"
      >
        <span className="truncate" title={displayText || undefined}>
          {displayText || <span className="text-muted-foreground">Not linked</span>}
        </span>
        <ChevronDown className="h-4 w-4 opacity-50 shrink-0" />
      </button>
    );
  }
  return (
    <Select defaultOpen value={value} onValueChange={onChange} onOpenChange={(o) => { if (!o) onCancelEdit(); }}>
      <SelectTrigger
        aria-label={ariaLabel}
        data-testid={`${testId}-select`}
        className="bg-input border-border h-9 rounded-xl focus-visible:ring-2 focus-visible:ring-primary/60"
      >
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {options.map((op) => <SelectItem key={op.value} value={op.value}>{op.label}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}

const chauffeurLabel = (c) => (c ? `${c.first_name || ""} ${c.last_name || ""}`.trim() || c.email || c.id : "");

// Each row is memoized: revealing one cell's dropdown, or patching one account
// after a save, re-renders only the rows that actually changed.
const Row = memo(function Row({
  u, currentUserId, editingField,
  salespeople, chauffeurs, carOwners,
  onStartEdit, onCancelEdit, onCellChange,
  onOpenAccess, onResetPassword, onSignOut, onDelete,
}) {
  const driver = chauffeurs.find((c) => c.id === u.chauffeur_id);
  const owner = carOwners.find((o) => o.id === u.investor_owner_id);

  const spOptions = editingField === "salesperson"
    ? [{ value: "none", label: "Not linked" }, ...salespeople.map((sp) => ({ value: sp.name, label: sp.name }))]
    : null;
  const chOptions = editingField === "chauffeur"
    ? [{ value: "none", label: "Not linked" }, ...chauffeurs.map((c) => ({ value: c.id, label: chauffeurLabel(c) }))]
    : null;
  const coOptions = editingField === "owner"
    ? [{ value: "none", label: "Not linked" }, ...carOwners.map((o) => ({ value: o.id, label: o.name }))]
    : null;

  return (
    <tr className="border-b border-border/40" data-testid={`user-row-${u.id}`}>
      <td className="px-6 py-3 font-heading">{u.name}</td>
      <td className="px-6 py-3">{u.email}</td>
      <td className="px-6 py-3">
        <span className={`label-mono ${u.role === "admin" ? "text-primary" : ""}`}>{u.role}</span>
        {u.must_change_password && <span className="ml-2 text-[10px] uppercase tracking-wider text-amber-400" data-testid={`user-must-change-${u.id}`}>● temp pw</span>}
      </td>
      <td className="px-6 py-3">
        <LinkCell
          isEditing={editingField === "salesperson"}
          value={u.salesperson || "none"}
          displayText={u.salesperson || ""}
          options={spOptions}
          onStartEdit={() => onStartEdit(u.id, "salesperson")}
          onCancelEdit={onCancelEdit}
          onChange={(v) => onCellChange(u.id, "salesperson", v)}
          ariaLabel={`Salesperson for ${u.name || u.email}`}
          testId={`user-salesperson-${u.id}`}
        />
      </td>
      <td className="px-6 py-3">
        <LinkCell
          isEditing={editingField === "chauffeur"}
          value={u.chauffeur_id || "none"}
          displayText={chauffeurLabel(driver)}
          options={chOptions}
          onStartEdit={() => onStartEdit(u.id, "chauffeur")}
          onCancelEdit={onCancelEdit}
          onChange={(v) => onCellChange(u.id, "chauffeur", v)}
          ariaLabel={`Driver for ${u.name || u.email}`}
          testId={`user-chauffeur-${u.id}`}
        />
      </td>
      <td className="px-6 py-3">
        <LinkCell
          isEditing={editingField === "owner"}
          value={u.investor_owner_id || "none"}
          displayText={owner?.name || ""}
          options={coOptions}
          onStartEdit={() => onStartEdit(u.id, "owner")}
          onCancelEdit={onCancelEdit}
          onChange={(v) => onCellChange(u.id, "owner", v)}
          ariaLabel={`Car owner for ${u.name || u.email}`}
          testId={`user-investor-${u.id}`}
        />
      </td>
      <td className="px-6 py-3 text-right">
        {u.role !== "admin" && (
          <Button size="sm" variant="ghost" onClick={() => onOpenAccess(u)} title="Edit operator — details, account type, allowances, password" data-testid={`user-access-${u.id}`}>
            <PencilSimple size={14} className="text-primary" />
          </Button>
        )}
        {/* SALES-015: an admin row gets the password path and nothing else. */}
        {u.role === "admin" && (
          <Button size="sm" variant="ghost" onClick={() => onResetPassword(u)} title="Reset this admin's password" data-testid={`user-reset-pw-${u.id}`}>
            <Lock size={14} className="text-primary" />
          </Button>
        )}
        {u.id !== currentUserId && (
          <Button size="sm" variant="ghost" onClick={() => onSignOut(u)} title="Sign this account out on every device" data-testid={`user-signout-${u.id}`}>
            <SignOut size={14} className="text-amber-400" />
          </Button>
        )}
        {u.id !== currentUserId && (
          <Button size="sm" variant="ghost" onClick={() => onDelete(u.id)} data-testid={`user-delete-${u.id}`}>
            <Trash size={14} className="text-destructive" />
          </Button>
        )}
      </td>
    </tr>
  );
});

// The table is memoized on its data + callbacks. Because none of the six
// dialogs' form state is passed in, typing anywhere on the page re-renders the
// parent but skips this whole subtree.
export const TeamAccessTable = memo(function TeamAccessTable({
  users, salespeople, chauffeurs, carOwners, loading, currentUserId,
  onOpenAccess, onResetPassword, onSignOut, onDelete,
  onAssignSalesperson, onAssignChauffeur, onAssignOwner,
}) {
  const [editingCell, setEditingCell] = useState(null);
  const startEdit = useCallback((rowId, field) => setEditingCell({ rowId, field }), []);
  const cancelEdit = useCallback(() => setEditingCell(null), []);
  const handleCellChange = useCallback((rowId, field, value) => {
    setEditingCell(null);
    if (field === "salesperson") onAssignSalesperson(rowId, value);
    else if (field === "chauffeur") onAssignChauffeur(rowId, value);
    else if (field === "owner") onAssignOwner(rowId, value);
  }, [onAssignSalesperson, onAssignChauffeur, onAssignOwner]);

  return (
    // SALES-015: seven columns need room. Scroll inside the card plus a min-width
    // so the columns do not crush.
    <div className="overflow-x-auto" data-testid="team-access-table-wrap">
      <table className="w-full text-sm min-w-[1200px]">
        <thead>
          <tr className="border-b border-border">
            <th className="text-left px-6 py-3 label-mono font-normal">Name</th>
            <th className="text-left px-6 py-3 label-mono font-normal">Email</th>
            <th className="text-left px-6 py-3 label-mono font-normal">Role</th>
            <th className="text-left px-6 py-3 label-mono font-normal w-64">Salesperson (My Bookings)</th>
            <th className="text-left px-6 py-3 label-mono font-normal w-64">Driver (My Schedule)</th>
            <th className="text-left px-6 py-3 label-mono font-normal w-64">Car owner (Investor)</th>
            <th className="text-right px-6 py-3 label-mono font-normal w-24">Actions</th>
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <tr><td colSpan={7} className="p-10 text-center text-muted-foreground">Loading…</td></tr>
          ) : users.map((u) => (
            <Row
              key={u.id}
              u={u}
              currentUserId={currentUserId}
              editingField={editingCell?.rowId === u.id ? editingCell.field : null}
              salespeople={salespeople}
              chauffeurs={chauffeurs}
              carOwners={carOwners}
              onStartEdit={startEdit}
              onCancelEdit={cancelEdit}
              onCellChange={handleCellChange}
              onOpenAccess={onOpenAccess}
              onResetPassword={onResetPassword}
              onSignOut={onSignOut}
              onDelete={onDelete}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
});
